import i18n from '../i18n';

import { uiDocLang } from '../utils/docLang';
const BASE = process.env.REACT_APP_API_URL || '/api';

async function uploadOrgDoc(path, file) {
  const token = getToken();
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(path, { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Upload failed');
  return data;
}

// Ручки, которые работают БЕЗ сессии. Заголовок Authorization на них не идёт
// вовсе — см. коммент к getToken(): битый токен в localStorage не должен
// ронять вход, иначе человек заперт на экране логина и вылечить это можно
// только очисткой данных сайта.
const PUBLIC_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/forgot',
  '/auth/verify-otp',
  '/auth/reset-password',
  '/auth/invite',        // /auth/invite/:token и /auth/invite/accept
  '/auth/handoff/exchange',
  '/branding',
];

function isPublicPath(path) {
  const clean = path.split('?')[0];
  return PUBLIC_PATHS.some((p) => clean === p || clean.startsWith(`${p}/`));
}

/**
 * Токен из localStorage, пригодный для заголовка HTTP.
 *
 * ⚠️ Валидация не паранойя. Значение уходит в `Authorization`, а fetch()
 * проверяет заголовок СИНХРОННО и на недопустимом символе (перевод строки,
 * пробел, кириллица) кидает TypeError ДО того, как запрос уйдёт в сеть.
 * В Safari это выглядит как «The string did not match the expected pattern.»
 * в форме логина: сервер ни при чём, пароль верный, а войти нельзя — потому
 * что запрос не состоялся. Порченое значение чиним на месте: оно всё равно
 * ни одну ручку не откроет.
 */
function getToken() {
  let token;
  try {
    token = localStorage.getItem('pm_token');
  } catch {
    return null; // приватный режим Safari умеет отказывать в доступе к storage
  }
  if (token == null) return null;

  const clean = token.trim();
  // RFC 7230 token68 + разделители: печатный US-ASCII без пробелов.
  if (clean && /^[\x21-\x7E]+$/.test(clean)) return clean;

  try { localStorage.removeItem('pm_token'); } catch { /* см. выше */ }
  return null;
}

async function request(method, path, body) {
  const headers = { 'Content-Type': 'application/json' };
  const token = isPublicPath(path) ? null : getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && path !== '/auth/login' && path !== '/auth/me') {
    localStorage.removeItem('pm_token');
    // Сессия завершена сменой пароля (token_version bump) — показываем причину на логине
    const d = await res.clone().json().catch(() => ({}));
    window.location.href = d.code === 'SESSION_EXPIRED' ? '/login?reason=session_expired' : '/login';
    return;
  }

  const data = await res.json();

  // Аккаунт удалён суперадмином — немедленно выкидываем на логин
  if (res.status === 403 && data.code === 'ACCOUNT_DELETED') {
    localStorage.removeItem('pm_token');
    window.location.href = '/login?reason=deleted';
    return;
  }

  // Демо-сессия истекла на бэке — кикаем на лендинг с маркером
  if (res.status === 410 && data.code === 'DEMO_EXPIRED') {
    localStorage.removeItem('pm_token');
    window.location.href = '/?reason=demo_expired';
    return;
  }

  // Подписка приостановлена (read-only) — показываем переведённый текст, не сырой с бэка
  if (res.status === 403 && data.code === 'SUBSCRIPTION_SUSPENDED') {
    const err = new Error(i18n.t('billing.errors.suspendedAction'));
    err.status = res.status;
    err.code = data.code;
    throw err;
  }

  // Лимит тарифа (флекси-биллинг, фаза 4) — переведённый текст с цифрами пакета.
  if (res.status === 403 && data.code === 'TARIFF_LIMIT') {
    const err = new Error(i18n.t('billing.errors.tariffLimit', {
      current: data.current, included: data.included,
      what: i18n.t(`billing.limit.what.${data.meter}`, { defaultValue: data.meter }),
    }));
    err.status = res.status;
    err.code = data.code;
    err.body = data;
    throw err;
  }

  if (!res.ok) {
    const err = new Error(data.error || 'Ошибка запроса');
    err.status = res.status;
    // Полное тело ответа: у 409 бэкенд кладёт в него подсказку, чем разрулить
    // конфликт (existing_id/existing_code у единиц, usage у занятых записей).
    // Перечислять такие поля по одному — путь к бесконечному списку ниже.
    err.body = data;
    if (data.code) err.code = data.code;
    if (data.conflict) { err.conflict = true; err.matches = data.matches; }
    if (data.greeted_by_name !== undefined) err.greeted_by_name = data.greeted_by_name;
    if (data.greeted_at !== undefined) err.greeted_at = data.greeted_at;
    throw err;
  }
  return data;
}

// Плоский набор параметров клиентского превью — общий для GET-превью и POST тест-отправки.
// Булевы кодируются как '1'/'0', вложенные выборы полей — CSV включённых id.
function buildEmailPreviewParams({ tpl, lang, accent, bg, logo, tagline, texts, blocks }) {
  const p = {};
  if (tpl)  p.tpl = tpl;
  if (lang) p.lang = lang;
  if (accent) p.accent = accent;
  if (bg)     p.bg = bg;
  if (logo != null)    p.logo = logo ? '1' : '0';
  if (tagline != null) p.tagline = tagline;
  if (texts) for (const f of ['subject', 'heading', 'eyebrow', 'intro', 'closing', 'signature', 'cta_label', 'promo_text', 'promo_note']) {
    if (texts[f] != null && texts[f] !== '') p[f] = texts[f];
  }
  if (blocks) {
    for (const [k, qk] of [['info_rows', 'b_info_rows'], ['cta', 'b_cta'], ['amount', 'b_amount'], ['promo', 'b_promo'], ['amount_with_vat', 'b_amount_with_vat']]) {
      if (blocks[k] != null) p[qk] = blocks[k] ? '1' : '0';
    }
    if (blocks.promo_code != null) p.promo_code = blocks.promo_code;
    if (blocks.cta_action != null) p.cta_action = blocks.cta_action;
    for (const grp of ['info_fields', 'amount_fields']) {
      if (blocks[grp] && typeof blocks[grp] === 'object') {
        p[grp] = Object.keys(blocks[grp]).filter(id => blocks[grp][id] !== false).join(',');
      }
    }
  }
  return p;
}

/**
 * Поток событий Канала (SSE) — через fetch, а не EventSource: тот не умеет
 * слать заголовок Authorization, а токен в URL оседал бы в логах прокси.
 * Возвращает Response; читать через res.body.getReader().
 */
export function openChannelStream(signal) {
  const token = getToken();
  return fetch(`${BASE}/channel/stream`, {
    headers: { Accept: 'text/event-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    signal,
    cache: 'no-store',
  });
}

export const api = {
  // Generic helpers
  post:   (path, body) => request('POST',   path, body),
  get:    (path)       => request('GET',    path),
  patch:  (path, body) => request('PATCH',  path, body),
  put:    (path, body) => request('PUT',    path, body),
  delete: (path)       => request('DELETE', path),

  // Zoho Books (интеграция инвойсов)
  zohoStatus:        ()       => request('GET',  '/integrations/zoho/status'),
  zohoConnectUrl:    ()       => request('GET',  '/integrations/zoho/connect-url'),
  zohoOrganizations: ()       => request('GET',  '/integrations/zoho/organizations'),
  zohoSelectOrg:     (data)   => request('POST', '/integrations/zoho/select-org', data),
  zohoDisconnect:    ()       => request('POST', '/integrations/zoho/disconnect'),
  zohoCreateInvoice: (data)   => request('POST', '/integrations/zoho/invoice', data),
  zohoInvoices:      (taskId) => request('GET',  `/integrations/zoho/invoices${taskId ? `?task_id=${taskId}` : ''}`),
  zohoInvoicesByProperty: (propertyId) => request('GET', `/integrations/zoho/invoices?property_id=${propertyId}`),
  // Счета по AMC-контракту. Берём тем же эндпоинтом, что и по Работе, — карточке
  // счёта нужны ссылка в Zoho, позиции и получатель, а не три поля из amc.js.
  zohoInvoicesByAmc: (contractId) => request('GET', `/integrations/zoho/invoices?amc_contract_id=${contractId}`),
  zohoSyncInvoice:   (id)     => request('POST', `/integrations/zoho/invoices/${id}/sync`),
  zohoVoidInvoice:   (id)     => request('POST', `/integrations/zoho/invoices/${id}/void`),
  // Дефолт орги при выставлении: 'sent' (сразу выставить) | 'draft' (черновик в Zoho)
  zohoSetInvoiceDefault: (mode) => request('PUT', '/integrations/zoho/invoice-default', { mode }),
  // Справочник налогов Zoho и выбор налога организации. Ставка проставляется на
  // КАЖДУЮ позицию инвойса — без неё Zoho ставит свой дефолт (Exempt).
  zohoTaxes:         ()       => request('GET',  '/integrations/zoho/taxes'),
  zohoSetTax:        (taxId)  => request('PUT',  '/integrations/zoho/tax', { tax_id: taxId || null }),
  // Письмо клиенту со счётом: шаблон «Коммуникаций» + PDF из Zoho вложением
  // data: { to: [emails], copy_self } — получатели из модала отправки
  zohoSendInvoice:   (id, data = {}) => request('POST', `/integrations/zoho/invoices/${id}/send`, data),
  // Превью письма со счётом (тема + HTML + имя вложения) — как у отчёта
  // lang — разовое переключение языка письма в превью (иначе язык коммуникаций клиента)
  zohoInvoiceSendPreview: (id, lang) => request('GET', `/integrations/zoho/invoices/${id}/send-preview${lang ? `?lang=${lang}` : ''}`),
  // PDF инвойса из Zoho (проксируем, не храним). Возвращает { blob, filename }.
  zohoInvoicePdf: async (id) => {
    const token = getToken();
    const res = await fetch(`${BASE}/integrations/zoho/invoices/${id}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.error || 'Не удалось получить PDF инвойса');
    }
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename\*=UTF-8''(.+)/) || cd.match(/filename="?([^"]+)"?/);
    const filename = m ? decodeURIComponent(m[1]) : `invoice_${id}.pdf`;
    return { blob: await res.blob(), filename };
  },

  // Черновик формы начисления по Работе (кнопка «Сохранить» в блоке «Доходы»)
  getIncomeDraft:    (taskId) => request('GET',    `/income/draft/${taskId}`),
  saveIncomeDraft:   (taskId, payload) => request('PUT', `/income/draft/${taskId}`, { payload }),
  deleteIncomeDraft: (taskId) => request('DELETE', `/income/draft/${taskId}`),

  // Перевод произвольных текстов «на лету» (превью в форме до сохранения).
  // data: { texts: [string|null], target: 'ru'|'en'|'ar'|'hi'|'ur', source? }
  translateBatch:    (data)   => request('POST', '/translate/batch', data),

  // Интеграции — внешние «Заявки» (Do Do Desk/Naumen и пр.)
  getExternalRequests: () => request('GET', '/integrations/requests'),
  getExternalRequest: (id) => request('GET', `/integrations/requests/${id}`),
  takeExternalRequest: (id) => request('POST', `/integrations/requests/${id}/take`),
  setExternalRequestStatus: (id, data) => request('POST', `/integrations/requests/${id}/status`, data),
  getExternalRequestTransitions: (id) => request('GET', `/integrations/requests/${id}/transitions`),
  getRequestReportDraft: (id) => request('GET', `/integrations/requests/${id}/report-draft`),
  sendRequestReport: (id, data) => request('POST', `/integrations/requests/${id}/send-report`, data),
  getRequestReport: (id, reportId) => request('GET', `/integrations/requests/${id}/reports/${reportId}`),
  setRequestAttachmentComment: (id, data) => request('PATCH', `/integrations/requests/${id}/attachment`, data),
  getRequestComments: (id) => request('GET', `/integrations/requests/${id}/comments`),
  addRequestComment: (id, data) => request('POST', `/integrations/requests/${id}/comments`, data),
  markRequestCommentsSeen: (id) => request('POST', `/integrations/requests/${id}/comments/seen`),
  // Контакт Do Do → контакт Объекта заявки. match отдаёт похожих уже заведённых
  // (пробу сервер берёт из самой заявки); addContact создаёт нового или связывает
  // существующего по client_id. Точное совпадение email/телефона → 409 duplicate_client.
  getRequestContactMatch: (id) => request('GET', `/integrations/requests/${id}/contact-match`),
  addRequestContact: (id, data) => request('POST', `/integrations/requests/${id}/contact`, data),
  // Связывание существующих Работ с Заявкой (заявки приходят задним числом)
  getRequestLinkCandidates: (id) => request('GET', `/integrations/requests/${id}/link-candidates`),
  linkRequestTasks: (id, data) => request('POST', `/integrations/requests/${id}/link-tasks`, data),
  unlinkRequestTask: (id, taskId) => request('DELETE', `/integrations/requests/${id}/link-tasks/${taskId}`),
  integrationStatus: (source) => request('GET', `/integrations/connections/${source}`),
  integrationConnect: (source, data) => request('POST', `/integrations/connections/${source}/connect`, data),
  integrationDisconnect: (source) => request('POST', `/integrations/connections/${source}/disconnect`),
  integrationTest: (source) => request('POST', `/integrations/connections/${source}/test`),
  integrationRetryFailed: (source) => request('POST', `/integrations/connections/${source}/retry-failed`),
  integrationEvents: (source) => request('GET', `/integrations/connections/${source}/events`),
  integrationUndelivered: (source) => request('GET', `/integrations/connections/${source}/undelivered`),
  integrationObjectMap: (source) => request('GET', `/integrations/connections/${source}/object-map`),
  integrationSetObjectMap: (source, data) => request('POST', `/integrations/connections/${source}/object-map`, data),
  integrationRequestsByProperty: (propertyId) => request('GET', `/integrations/requests/by-property/${propertyId}`),

  // Оборудование Объекта
  equipmentList: (propertyId, lang) => request('GET', `/equipment?property_id=${propertyId}&lang=${lang || 'ru'}`),
  // scanToken — код с наклейки. Открывает карточку на чтение, когда Объекта нет
  // в области данных: сканирующий стоит перед железом, и это его пропуск.
  equipmentGet: (id, scanToken) => request('GET', `/equipment/${id}${scanToken ? `?scan_token=${encodeURIComponent(scanToken)}` : ''}`),
  equipmentCreate: (data) => request('POST', '/equipment', data),
  equipmentUpdate: (id, data) => request('PATCH', `/equipment/${id}`, data),
  equipmentDelete: (id, reason) => request('DELETE', `/equipment/${id}`, reason ? { reason } : undefined),
  equipmentFleetArchived: (lang) => request('GET', `/equipment/fleet/archived?lang=${lang || 'ru'}`),
  equipmentArchivePreview: (id) => request('GET', `/equipment/${id}/archive-preview`),
  equipmentArchived: (propertyId, lang) => request('GET', `/equipment/archived?property_id=${propertyId}&lang=${lang || 'ru'}`),
  equipmentRestore: (id) => request('POST', `/equipment/${id}/restore`),
  equipmentConfirm: (id) => request('POST', `/equipment/${id}/confirm`),
  equipmentConfirmBulk: (ids) => request('POST', '/equipment/confirm-bulk', { ids }),
  equipmentMerge: (id, targetId) => request('POST', `/equipment/${id}/merge`, { target_id: targetId }),
  equipmentUnconfirmedCount: () => request('GET', '/equipment/unconfirmed-count'),
  equipmentUnconfirmed: () => request('GET', '/equipment/unconfirmed'),
  equipmentReject: (id) => request('POST', `/equipment/${id}/reject`),
  equipmentPresign: (contentType, kind) => request('POST', '/equipment/presign', { contentType, kind }),
  equipmentRecognizePlate: (key) => request('POST', '/equipment/recognize-plate', { key }),
  equipmentResolveTag: (token, source) => request('GET', `/equipment/tags/resolve/${token}?source=${source || 'in_app'}`),
  equipmentBindTag: (token, equipmentId) => request('POST', `/equipment/tags/${token}/bind`, { equipment_id: equipmentId }),
  equipmentTagBatch: (data) => request('POST', '/equipment/tags/batch', data),
  equipmentTagStats: () => request('GET', '/equipment/tags/stats'),
  equipmentRevokeTag: (token) => request('POST', `/equipment/tags/${token}/revoke`),
  equipmentPassportSettings: () => request('GET', '/equipment/passport-settings'),
  equipmentPassportSave: (data) => request('PUT', '/equipment/passport-settings', data),
  equipmentReportSettings: () => request('GET', '/equipment/report-settings'),
  equipmentReportSettingsSave: (data) => request('PUT', '/equipment/report-settings', data),
  // Реестр кодов. Живёт на своём флаге audit_tags и НЕ скоупится по бригадам —
  // это инвентаризация, а не рабочий список.
  equipmentTagList: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== '' && v != null)
    ).toString();
    return request('GET', `/equipment/tags${qs ? `?${qs}` : ''}`);
  },
  // Лист наклеек под резку. Ручка за авторизацией, поэтому не <a href>, а fetch
  // с токеном → blob. Отдаём его вызывающему: на iOS простое скачивание не
  // работает, там нужен navigator.share (см. utils/fileShare.js).
  // Допечатка выбранных кодов: с объекта пришло «одна наклейка отвалилась»,
  // и печатать ради неё всю партию — это выброшенные листы.
  // lang — явный выбор в моменте печати (см. LangToggle в реестре), не
  // обязателен: без него бэк берёт язык партии, к которой относятся коды.
  equipmentTagSheetFor: async (tokens, lang) => {
    const token = getToken();
    const res = await fetch(`${BASE}/equipment/tags/sheet.pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ tokens, lang }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      const err = new Error(d.error || '');
      err.status = res.status; err.code = d.code;
      throw err;
    }
    const ct = res.headers.get('Content-Type') || '';
    if (!ct.includes('application/pdf')) throw new Error(`Сервер вернул не PDF (${ct || 'без типа'})`);
    // Имя строит сервер (домен орги + номер партии + дата выпуска, см.
    // routes/equipment.js:tagsFilename) — здесь просто читаем заголовок,
    // с фолбэком на случай старого бэкенда без X-Filename.
    const filename = res.headers.get('X-Filename') || `fixty-tags-${tokens.length}.pdf`;
    return { blob: await res.blob(), filename };
  },
  // lang — явное переопределение НА ЭТУ печать (см. LangToggle у строки
  // партии); без него бэк берёт язык, замороженный на партии при выпуске.
  equipmentTagSheet: async (batchId, lang) => {
    const token = getToken();
    const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
    const res = await fetch(`${BASE}/equipment/tags/batch/${batchId}/sheet.pdf${qs}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      const err = new Error(d.error || '');
      err.status = res.status;
      err.code = d.code;
      throw err;
    }
    // ⚠️ Проверяем, что приехал именно PDF. Ручка уже один раз отдавала 200 с
    // JSON-телом (Uint8Array из Puppeteer, сериализованный Express), и клиент
    // послушно заворачивал его в blob с расширением .pdf. Пользователь узнавал
    // о поломке только от Просмотра — «файл повреждён», без единой зацепки.
    // Дешевле проверить заголовок здесь, чем ловить это на объекте.
    const ct = res.headers.get('Content-Type') || '';
    if (!ct.includes('application/pdf')) {
      throw new Error(`Сервер вернул не PDF (${ct || 'без типа'})`);
    }
    const filename = res.headers.get('X-Filename') || `fixty-tags-${batchId}.pdf`;
    return { blob: await res.blob(), filename };
  },
  equipmentByTask: (taskId) => request('GET', `/equipment/by-task/${taskId}`),
  equipmentLink: (taskId, equipmentId) => request('POST', '/equipment/link', { task_id: taskId, equipment_id: equipmentId }),
  equipmentUnlink: (taskId, equipmentId) => request('DELETE', `/equipment/link/${taskId}/${equipmentId}`),
  // Перемещение Склад ↔ Объекты (equipment_mobility_plan.md)
  equipmentWarehouses: () => request('GET', '/equipment/warehouses'),
  equipmentMovePreview: (id) => request('GET', `/equipment/${id}/move-preview`),
  equipmentMove: (id, data) => request('POST', `/equipment/${id}/move`, data),
  equipmentMoveBulk: (data) => request('POST', '/equipment/move-bulk', data),
  // Статус переносной единицы (фаза 4): to = 'ready' | 'off_hire'.
  equipmentSetStatus: (id, to, note, overrideReason) => request('POST', `/equipment/${id}/status`, { to, note, override_reason: overrideReason }),
  equipmentPast: (propertyId, lang) => request('GET', `/equipment/past?property_id=${propertyId}&lang=${lang || 'ru'}`),
  equipmentFleet: (lang) => request('GET', `/equipment/fleet?lang=${lang || 'ru'}`),
  // Индикаторы Инвентаризации: { scope: 'user' | 'org', hidden: string[] | null }.
  equipmentFleetKpiConfig: (body) => request('PUT', '/equipment/fleet/kpi-config', body),
  equipmentFleetJournal: (from, to, lang) => request('GET', `/equipment/fleet/journal?from=${from}&to=${to}&lang=${lang || 'ru'}`),

  // Auth
  login: (email, password) => request('POST', '/auth/login', { email, password }),
  register: (name, email, password) => request('POST', '/auth/register', { name, email, password }),
  me: () => request('GET', '/auth/me'),
  updateMe: (data) => request('PUT', '/auth/me', data),

  // Clients
  // archived: undefined — только активные (дефолт) | 'only' — архив | 'all'
  getClients: (archived) => request('GET', archived ? `/clients?archived=${archived}` : '/clients'),
  getClient: (id) => request('GET', `/clients/${id}`),
  createClient: (data) => request('POST', '/clients', data),
  updateClient: (id, data) => request('PUT', `/clients/${id}`, data),
  deleteClient: (id) => request('DELETE', `/clients/${id}`),
  // Архивация: клиента с финансовой историей удалить нельзя (409
  // has_financial_history) — только убрать из списков и пикеров.
  getClientDeleteImpact: (id) => request('GET', `/clients/${id}/delete-impact`),
  archiveClient: (id) => request('POST', `/clients/${id}/archive`),
  unarchiveClient: (id) => request('POST', `/clients/${id}/unarchive`),

  // Поздравление с ДР: превью письма (с несохранёнными правками) и отправка клиенту.
  getBirthdayGreetingPreview: (id, { lang, client_name, custom_message, signature, promo, promo_text, promo_note, promo_code, cta }) => {
    const p = new URLSearchParams();
    if (lang) p.set('lang', lang);
    if (client_name) p.set('client_name', client_name);
    if (custom_message) p.set('custom_message', custom_message);
    if (signature) p.set('signature', signature);
    if (promo != null) p.set('promo', promo ? '1' : '0');
    if (promo_text) p.set('promo_text', promo_text);
    if (promo_note) p.set('promo_note', promo_note);
    if (promo_code) p.set('promo_code', promo_code);
    if (cta != null) p.set('cta', cta ? '1' : '0');
    return request('GET', `/clients/${id}/birthday-greeting/preview?${p.toString()}`);
  },
  getBirthdayGreetingSenderInfo: (id) =>
    request('GET', `/clients/${id}/birthday-greeting/sender-info`),
  getBirthdayGreetingStatus: (id) =>
    request('GET', `/clients/${id}/birthday-greeting/status`),
  sendBirthdayGreeting: (id, { lang, client_name, custom_message, signature, promo, promo_text, promo_note, promo_code, cta, from_self, force }) =>
    request('POST', `/clients/${id}/birthday-greeting`, { lang, client_name, custom_message, signature, promo, promo_text, promo_note, promo_code, cta, from_self, force }),

  // Клиентские письма, привязанные к Работе (completion / invoice; reminder — превью).
  getTaskEmailStatus: (taskId) =>
    request('GET', `/tasks/${taskId}/client-email/status`),
  getTaskEmailPreview: (taskId, { template, lang, client_name, custom_message, signature, info_overrides, amount_due, due_date, cta }) => {
    const p = new URLSearchParams();
    if (template) p.set('template', template);
    if (lang) p.set('lang', lang);
    if (client_name) p.set('client_name', client_name);
    if (custom_message) p.set('custom_message', custom_message);
    if (signature) p.set('signature', signature);
    if (info_overrides && Object.keys(info_overrides).length) p.set('info_overrides', JSON.stringify(info_overrides));
    if (amount_due) p.set('amount_due', amount_due);
    if (due_date) p.set('due_date', due_date);
    if (cta != null) p.set('cta', cta ? '1' : '0');
    return request('GET', `/tasks/${taskId}/client-email/preview?${p.toString()}`);
  },
  sendTaskEmail: (taskId, body) =>
    request('POST', `/tasks/${taskId}/client-email/send`, body),
  // Bill-to реквизиты (выставление инвойсов)
  getClientBilling: (id) => request('GET', `/clients/${id}/billing`),
  updateClientBilling: (id, data) => request('PUT', `/clients/${id}/billing`, data),
  // Создать/привязать или обновить контакт клиента в Zoho (ручной push из карточки)
  pushClientZohoContact: (id) => request('POST', `/clients/${id}/zoho-contact`),

  presignClientAvatar: (id, contentType, variant) => request('POST', `/clients/${id}/avatar/presign`, { contentType, variant }),
  confirmClientAvatar: (id, key, thumbKey) => request('POST', `/clients/${id}/avatar`, { key, thumbKey }),
  deleteClientAvatar: (id) => request('DELETE', `/clients/${id}/avatar`),

  // Calendar feed (ICS)
  getCalendarFeedSettings: () => request('GET', '/calendar/settings'),
  regenerateCalendarFeed: () => request('POST', '/calendar/regenerate'),
  setCalendarFeedScope: (scope) => request('PUT', '/calendar/scope', { scope }),

  // User avatar
  presignUserAvatar: (contentType, variant) => request('POST', '/auth/me/avatar/presign', { contentType, variant }),
  confirmUserAvatar: (key, thumbKey) => request('POST', '/auth/me/avatar', { key, thumbKey }),
  deleteUserAvatar: () => request('DELETE', '/auth/me/avatar'),

  // Emirates ID (end-to-end encrypted)
  getEidMeta:            ()           => request('GET',    '/documents/emirates-id'),
  getEidVault:           ()           => request('GET',    '/documents/emirates-id/vault'),
  presignEidOcr:         (contentType) => request('POST',  '/documents/emirates-id/ocr-presign', { contentType }),
  runEidOcr:             (temp_key)   => request('POST',   '/documents/emirates-id/ocr', { temp_key }),
  presignEidUpload:      (side)       => request('POST',   '/documents/emirates-id/upload-presign', { side }),
  confirmEid:            (data)       => request('POST',   '/documents/emirates-id/confirm', data),
  deleteEid:             ()           => request('DELETE', '/documents/emirates-id'),

  // Org EID key pair (admin setup)
  getOrgEidSetup:        ()           => request('GET',    '/documents/org/eid-setup'),
  setupOrgEidKeys:       (data)       => request('POST',   '/documents/org/eid-setup', data),
  getOrgEidPrivkey:      ()           => request('GET',    '/documents/org/eid-privkey'),

  // EID admin access management
  getEidAdminAccess:        ()        => request('GET',    '/documents/org/eid-admin-access'),
  getEidRecoveryBlob:       ()        => request('GET',    '/documents/org/eid-recovery-blob'),
  recoverEidAccess:         (data)    => request('POST',   '/documents/org/eid-recovery', data),
  revokeEidAdminAccess:     (adminId) => request('DELETE', `/documents/org/eid-grant/${adminId}`),
  createEidGrantInvite:     (data)    => request('POST',   '/documents/org/eid-grant-invite', data),
  getEidGrantInvite:        (token)   => request('GET',    `/documents/org/eid-grant-invite/${token}`),
  acceptEidGrantInvite:     (token, data) => request('POST', `/documents/org/eid-grant-invite/${token}/accept`, data),

  // Worker consent
  getConsentStatus:      ()           => request('GET',    '/documents/emirates-id/consent/status'),
  grantConsent:          (data)       => request('POST',   '/documents/emirates-id/consent', data),
  revokeConsent:         ()           => request('DELETE', '/documents/emirates-id/consent'),

  // Admin: team consent management
  getTeamConsents:       ()           => request('GET',    '/documents/team/eid-consents'),
  getWorkerConsent:      (userId)     => request('GET',    `/documents/team/eid-consents/${userId}`),
  presignConsentPhoto:   (side)       => request('POST',   '/documents/emirates-id/consent/photo-presign', { side }),

  // Org documents (Trade License, Insurance, VAT certificate) — server-side encrypted
  getOrgTL:     ()     => request('GET',    '/documents/org/tl'),
  deleteOrgTL:  ()     => request('DELETE', '/documents/org/tl'),
  patchOrgTL:   (data) => request('PATCH',  '/documents/org/tl', data),
  getOrgIns:    ()     => request('GET',    '/documents/org/ins'),
  deleteOrgIns: ()     => request('DELETE', '/documents/org/ins'),
  patchOrgIns:  (data) => request('PATCH',  '/documents/org/ins', data),
  getOrgVat:    ()     => request('GET',    '/documents/org/vat'),
  deleteOrgVat: ()     => request('DELETE', '/documents/org/vat'),
  patchOrgVat:  (data) => request('PATCH',  '/documents/org/vat', data),
  // РФ: Лист записи ЕГРЮЛ (аналог tl), Свидетельство ИНН (аналог vat).
  // Страховой полис переиспользует Ins — структура полей та же.
  getOrgEgrul:    ()     => request('GET',    '/documents/org/egrul'),
  deleteOrgEgrul: ()     => request('DELETE', '/documents/org/egrul'),
  patchOrgEgrul:  (data) => request('PATCH',  '/documents/org/egrul', data),
  getOrgInn:      ()     => request('GET',    '/documents/org/inn'),
  deleteOrgInn:   ()     => request('DELETE', '/documents/org/inn'),
  patchOrgInn:    (data) => request('PATCH',  '/documents/org/inn', data),
  // multipart uploads через fetch напрямую (не через обёртку)
  uploadOrgTL:    (file) => uploadOrgDoc('/api/documents/org/tl/upload', file),
  uploadOrgIns:   (file) => uploadOrgDoc('/api/documents/org/ins/upload', file),
  uploadOrgVat:   (file) => uploadOrgDoc('/api/documents/org/vat/upload', file),
  uploadOrgEgrul: (file) => uploadOrgDoc('/api/documents/org/egrul/upload', file),
  uploadOrgInn:   (file) => uploadOrgDoc('/api/documents/org/inn/upload', file),

  // PTW Operators (management companies)
  getPtwOperators: () => request('GET', '/ptw-operators'),
  createPtwOperator: (data) => request('POST', '/ptw-operators', data),
  suggestPtwEdit: (id, data) => request('POST', `/ptw-operators/${id}/suggest`, data),

  // PTW Requests (work permit flow)
  getPtwRequests: (params) => request('GET', `/ptw-requests?${new URLSearchParams(params)}`),
  createPtwRequest: (data) => request('POST', '/ptw-requests', data),
  updatePtwRequestStatus: (id, data) => request('PUT', `/ptw-requests/${id}/status`, data),
  composePtwEmail: (data) => request('POST', '/ptw-requests/compose', data),
  deletePtwRequest: (id) => request('DELETE', `/ptw-requests/${id}`),

  // Properties
  getProperties: (clientId) => request('GET', `/properties${clientId ? `?client_id=${clientId}` : ''}`),
  getProperty: (id) => request('GET', `/properties/${id}`),
  createProperty: (data) => request('POST', '/properties', data),
  updateProperty: (id, data) => request('PUT', `/properties/${id}`, data),
  deleteProperty: (id) => request('DELETE', `/properties/${id}`),
  getPropertyDeleteImpact: (id) => request('GET', `/properties/${id}/delete-impact`),

  // Контакты Объекта: несколько людей с ролями (владелец, бухгалтер, техкуратор).
  // Плательщик выводится из главного контакта на бэкенде, отдельным полем не шлётся.
  // Наборы bill-to реквизитов клиента. У одного клиента их может быть несколько
  // (физлицо и его компания) — каждый со своим контактом в Zoho.
  getClientBillingProfiles: (id) => request('GET', `/clients/${id}/billing-profiles`),
  createClientBillingProfile: (id, data) => request('POST', `/clients/${id}/billing-profiles`, data),
  updateClientBillingProfile: (id, pid, data) => request('PUT', `/clients/${id}/billing-profiles/${pid}`, data),
  deleteClientBillingProfile: (id, pid) => request('DELETE', `/clients/${id}/billing-profiles/${pid}`),
  pushBillingProfileToZoho: (id, pid) => request('POST', `/clients/${id}/billing-profiles/${pid}/zoho-contact`),
  // На какие реквизиты уйдёт счёт из этой точки входа + альтернативы.
  getBillingPreview: (params) => {
    const p = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => { if (v != null && v !== '') p.set(k, v); });
    return request('GET', `/integrations/zoho/billing-preview?${p.toString()}`);
  },

  getPropertyContacts: (id) => request('GET', `/properties/${id}/contacts`),
  addPropertyContact: (id, data) => request('POST', `/properties/${id}/contacts`, data),
  updatePropertyContact: (id, contactId, data) => request('PUT', `/properties/${id}/contacts/${contactId}`, data),
  deletePropertyContact: (id, contactId) => request('DELETE', `/properties/${id}/contacts/${contactId}`),
  presignPropertyCover: (id, variant) => request('POST', `/properties/${id}/cover/presign`, { variant }),
  // width/height — пропорции оригинала (до сжатия), по ним фронт выбирает раскладку шапки
  confirmPropertyCover: (id, key, thumbKey, width, height) => request('POST', `/properties/${id}/cover`, { key, thumbKey, width, height }),
  deletePropertyCover: (id) => request('DELETE', `/properties/${id}/cover`),

  // Tasks
  getTasks: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request('GET', `/tasks${q ? `?${q}` : ''}`);
  },
  getDashboard: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request('GET', `/tasks/dashboard${q ? `?${q}` : ''}`);
  },
  getTaskTitleSuggestions: (q) => request('GET', `/tasks/title-suggestions?q=${encodeURIComponent(q)}`),
  getTask: (id) => request('GET', `/tasks/${id}`),
  createTask: (data) => request('POST', '/tasks', data),

  // Мини-празднования: рубежи организации
  getPendingMilestone: (preview, metric) => request('GET', `/milestones/pending${preview
    ? `?preview=${encodeURIComponent(preview)}${metric ? `&metric=${encodeURIComponent(metric)}` : ''}`
    : ''}`),
  markMilestoneSeen: (id) => request('POST', `/milestones/${id}/seen`, {}),

  updateTask: (id, data) => request('PUT', `/tasks/${id}`, data),
  // «На согласовании» / «Отменена» (awaiting_client_implementation_plan.md)
  awaitTaskClient: (id, followUpDate) => request('POST', `/tasks/${id}/await-client`, { follow_up_date: followUpDate || null }),
  setTaskFollowUp: (id, followUpDate) => request('PATCH', `/tasks/${id}/follow-up`, { follow_up_date: followUpDate || null }),
  confirmTaskClient: (id, data) => request('POST', `/tasks/${id}/client-confirm`, data),
  declineTaskClient: (id, note) => request('POST', `/tasks/${id}/client-decline`, { note: note || undefined }),
  cancelTask: (id, note) => request('POST', `/tasks/${id}/cancel`, { note: note || undefined }),
  restoreTask: (id) => request('POST', `/tasks/${id}/restore`, {}),
  deleteTask: (id) => request('DELETE', `/tasks/${id}`),
  updateTaskAssignees: (id, assigneeIds) => request('PATCH', `/tasks/${id}/assignees`, { assignee_ids: assigneeIds }),
  markTaskViewed: (id) => request('PATCH', `/tasks/${id}/viewed`, {}),
  markTaskStarted: (id) => request('PATCH', `/tasks/${id}/started`, {}),
  pauseTask: (id) => request('PATCH', `/tasks/${id}/pause`, {}),
  resumeTask: (id) => request('PATCH', `/tasks/${id}/resume`, {}),
  // Админ/Бригадир отмечает чужого исполнителя. Тоггл: действие решает сервер
  // по текущему состоянию, клиент только указывает, кого переключить.
  toggleAssigneeStatus: (id, userId) => request('PATCH', `/tasks/${id}/assignee-status`, { user_id: userId }),
  takeTaskControl: (id) => request('PATCH', `/tasks/${id}/take-control`, {}),
  setTaskBilling: (id, data) => request('PATCH', `/tasks/${id}/billing`, data),
  changeCurator: (taskId, newCuratorId) => request('PATCH', `/tasks/${taskId}/curator`, { new_curator_id: newCuratorId }),
  offerCurator: (taskId, newCuratorId) => request('POST', `/tasks/${taskId}/curator-offer`, { new_curator_id: newCuratorId }),
  requestCuratorChange: (taskId) => request('POST', `/tasks/${taskId}/curator-change-request`, {}),
  acceptCuratorChange: (taskId) => request('POST', `/tasks/${taskId}/curator-change-request/accept`, {}),
  rejectCuratorChange: (taskId) => request('POST', `/tasks/${taskId}/curator-change-request/reject`, {}),

  // Comments
  addComment: (taskId, text) => request('POST', `/tasks/${taskId}/comments`, { text }),

  // Report
  getReportData: (taskId) => request('GET', `/tasks/${taskId}/report-data`),

  // Stats
  getStats: () => request('GET', '/stats'),

  // Settings — Роли и доступ (RBAC)
  getRoles: () => request('GET', '/roles'),
  // Роли для селекторов в Команде: без матрицы прав, доступны и Администратору
  getAssignableRoles: () => request('GET', '/settings/team/roles'),
  getRole: (id) => request('GET', `/roles/${id}`),
  createRole: (data) => request('POST', '/roles', data),
  updateRole: (id, data) => request('PUT', `/roles/${id}`, data),
  updateRoleMatrix: (id, permissions, flags) => request('PUT', `/roles/${id}/matrix`, { permissions, flags }),
  resetRole: (id) => request('POST', `/roles/${id}/reset`),
  deleteRole: (id) => request('DELETE', `/roles/${id}`),

  // Settings — Organization
  getOrganization: () => request('GET', '/settings/organization'),
  updateOrganization: (data) => request('PUT', '/settings/organization', data),
  setFinanalyticsEnabled: (enabled) => request('PUT', '/settings/organization/finanalytics', { enabled }),
  setOrgModules: (mods) => request('PUT', '/settings/organization/modules', mods),

  // Нумерация документов (Настройки → Организация). Чтение — org_settings.view,
  // правка — org_settings.edit.
  //
  // previewNumbering считает превью на СЕРВЕРЕ той же функцией, что выдаёт
  // реальный номер: своя копия форматтера на фронте разъехалась бы с серверной,
  // и тенант увидел бы корректный пример, а при сохранении получил отказ.
  getNumbering: () => request('GET', '/settings/numbering'),
  updateNumbering: (data) => request('PUT', '/settings/numbering', data),
  previewNumbering: (data) => request('POST', '/settings/numbering/preview', data),

  // Конструктор Онбординга (Настройки → Организация). Право — org_settings.edit.
  // getOnboardingConfig отдаёт наборы по всем четырём ролям сразу: конструктор
  // показывает их вкладками, дёргать эндпоинт на каждое переключение незачем.
  getOnboardingConfig: () => request('GET', '/onboarding/config'),
  setOnboardingConfig: (role, steps) => request('PUT', '/onboarding/config', { role, steps }),

  // Доходы — единый read-layer (ручные начисления + инвойсы интеграций)
  incomeTask:     (taskId)     => request('GET', `/income/task/${taskId}`),
  incomeProperty: (propertyId) => request('GET', `/income/property/${propertyId}`),
  incomeClient:   (clientId)   => request('GET', `/income/client/${clientId}`),
  incomeExpense:  (expenseId)  => request('GET', `/income/expense/${expenseId}`),
  incomeAwaiting: ()           => request('GET', '/income/awaiting'),
  getUninvoicedTaskIds: ()     => request('GET', '/tasks/uninvoiced'),
  getUnviewedTaskIds: ()       => request('GET', '/tasks/unviewed'),
  getWorkerNoPhotoTaskIds: ()  => request('GET', '/tasks/worker-no-photo-ids'),
  getWorkerNewTaskIds: ()      => request('GET', '/tasks/worker-new-ids'),

  // Ручной флоу «Доходов» — начисление и фиксация поступления (источник: finanalytics_entries)
  getFinanalytics:       (taskId)      => request('GET',    `/finanalytics/task/${taskId}`),
  createFinanalytics:    (data)        => request('POST',   '/finanalytics', data),
  updateFinanalytics:    (id, data)    => request('PATCH',  `/finanalytics/${id}`, data),
  confirmFinanalytics:   (id, date)    => request('PATCH',  `/finanalytics/${id}/confirm`, { received_date: date }),
  unconfirmFinanalytics: (id)          => request('PATCH',  `/finanalytics/${id}/unconfirm`),
  deleteFinanalytics:    (id)          => request('DELETE', `/finanalytics/${id}`),
  uploadLogo: (file, size = 'full') => {
    const token = getToken();
    const form = new FormData();
    form.append('logo', file);
    const sizeParam = size ? `?size=${size}` : '';
    return fetch(`${BASE}/settings/organization/logo${sizeParam}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    }).then(async r => {
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Ошибка загрузки');
      return d;
    });
  },
  deleteLogo: (size = 'full') => {
    const sizeParam = size ? `?size=${size}` : '';
    return request('DELETE', `/settings/organization/logo${sizeParam}`);
  },

  // Settings — Email sender (BYO Resend / Fixty)
  connectEmailByo: ({ api_key, from_address, from_name, reply_to }) =>
    request('POST', '/settings/organization/email-byo', { api_key, from_address, from_name, reply_to }),
  disconnectEmailByo: () => request('DELETE', '/settings/organization/email-byo'),
  setEmailMode: (mode) => request('POST', '/settings/organization/email-mode', { mode }),
  testEmail: () => request('POST', '/settings/email-test'),

  // Клиентские письма: тема и превью
  saveEmailBranding: ({ accent_color, bg_color, show_logo }) =>
    request('PUT', '/settings/organization/email-branding', { accent_color, bg_color, show_logo }),
  getEmailPreview: ({ tpl, lang, accent, bg, logo, tagline, texts, blocks }) => {
    const p = buildEmailPreviewParams({ tpl, lang, accent, bg, logo, tagline, texts, blocks });
    return request('GET', `/settings/organization/email-preview?${new URLSearchParams(p).toString()}`);
  },
  // Отправить тестовое клиентское письмо себе (с текущими, в т.ч. несохранёнными, настройками).
  sendEmailPreviewTest: ({ tpl, lang, accent, bg, logo, tagline, texts, blocks }) =>
    request('POST', '/settings/organization/email-preview-test',
      buildEmailPreviewParams({ tpl, lang, accent, bg, logo, tagline, texts, blocks })),
  getEmailTexts: ({ template, lang }) =>
    request('GET', `/settings/organization/email-texts?template=${encodeURIComponent(template)}&lang=${encodeURIComponent(lang)}`),
  saveEmailTexts: ({ template, lang, texts, tagline, translate, force }) =>
    request('PUT', '/settings/organization/email-texts', { template, lang, texts, tagline, translate, force }),
  saveEmailBlocks: ({ template, blocks }) =>
    request('PUT', '/settings/organization/email-blocks', { template, blocks }),

  // Web Push
  getPushVapidKey: () => request('GET', '/push/vapid-key'),
  pushSubscribe:   (data) => request('POST', '/push/subscribe', data),
  pushUnsubscribe: (data) => request('DELETE', '/push/subscribe', data),
  pushTest:        (data) => request('POST', '/push/test', data || {}),

  // Settings — Team
  getTeam: () => request('GET', '/settings/team'),
  inviteMember: (data) => request('POST', '/settings/team/invite', data),
  // role_id — любая роль организации, включая кастомные (см. routes/roles.js).
  // Строковый параметр role бэкенд ещё понимает, но это легаси: он знает только
  // 'admin' и 'worker' и молча схлопывает всё остальное в 'worker'.
  updateMemberRole: (userId, roleId) => request('PUT', `/settings/team/${userId}/role`, { role_id: Number(roleId) }),
  updateMemberPhone: (userId, phone) => request('PUT', `/settings/team/${userId}/phone`, { phone }),
  updateMemberCurator: (userId, curatorId) => request('PUT', `/settings/team/${userId}/curator`, { curator_id: curatorId }),
  // Команды сотрудников (teams_implementation_plan.md)
  getTeams: () => request('GET', '/settings/teams'),
  createTeam: (data) => request('POST', '/settings/teams', data),
  updateTeam: (id, data) => request('PUT', `/settings/teams/${id}`, data),
  deleteTeam: (id) => request('DELETE', `/settings/teams/${id}`),
  updateMemberTeam: (userId, teamId) => request('PUT', `/settings/team/${userId}/team`, { team_id: teamId }),
  // Пикер исполнителей: кого я могу назначить (+ «был на Объекте», если передан)
  getAssignable: (propertyId) => request('GET', `/team/assignable${propertyId ? `?property_id=${propertyId}` : ''}`),
  // Пустой массив = снять ограничение по городам (см. user_cities в БД).
  updateMemberCities: (userId, cityIds) => request('PUT', `/settings/team/${userId}/cities`, { city_ids: cityIds.map(Number) }),
  updateMemberStatus: (userId, status) => request('PUT', `/settings/team/${userId}/status`, { status }),
  getMemberPendingTasks: (userId) => request('GET', `/settings/team/${userId}/pending-tasks`),
  deleteMember: (userId) => request('DELETE', `/settings/team/${userId}`),
  resetMemberPassword: (userId) => request('POST', `/settings/team/${userId}/reset-password`),
  getMemberInviteLink: (userId) => request('POST', `/settings/team/${userId}/invite-link`),
  sendInviteEmail: (userId, inviteToken, isReset) => request('POST', '/settings/team/send-invite-email', { user_id: userId, invite_token: inviteToken, is_reset: isReset || false }),

  // Notifications
  getNotifications: () => request('GET', '/notifications'),
  markNotificationsRead: () => request('PATCH', '/notifications/read-all'),
  markNotificationRead: (id) => request('PATCH', `/notifications/${id}/read`),

  // Report Templates (конструктор отчётов)
  getReportTemplates: () => request('GET', '/report-templates'),
  createReportTemplate: (data) => request('POST', '/report-templates', data),
  updateReportTemplate: (id, data) => request('PUT', `/report-templates/${id}`, data),
  deleteReportTemplate: (id) => request('DELETE', `/report-templates/${id}`),
  // Шаблоны отчётов об инспекции — та же ручка, другой тип документа.
  // Фильтр на сервере: иначе шаблоны инспекции всплывут в выборе при отправке
  // акта о Работе, а блоки у них разные и документ соберётся пустым.
  // forChecklist: true — бланки чек-листа (осмотр оборудования), false — отчёт об инспекции Объекта,
  // не задан — все шаблоны инспекции (вкладка «Шаблоны» в Настройках).
  getInspectionTemplates: (forChecklist = null) => request('GET', `/report-templates?kind=inspection${forChecklist === null ? '' : `&for=${forChecklist ? 'checklist' : 'snag'}`}`),
  // Шаблоны отчёта по оборудованию Объекта — та же ручка, третий тип документа.
  getEquipmentTemplates: () => request('GET', '/report-templates?kind=equipment'),
  // lang — язык документа (тоггл в модале выбора шаблона). Не передали —
  // бэкенд возьмёт язык коммуникаций клиента, затем дефолт шаблона.
  downloadCustomReport: async (taskId, templateId, lang = null) => {
    const token = getToken();
    const res = await fetch(`${BASE}/tasks/${taskId}/custom-report.pdf?templateId=${templateId}${lang ? `&lang=${lang}` : ''}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      // Тело может быть и не JSON: при таймауте/обрыве отвечает прокси своей
      // HTML-страницей, и тогда сообщения от бэкенда просто нет. Пробрасываем
      // статус — вызывающий покажет разный текст для таймаута и для отказа.
      const d = await res.json().catch(() => ({}));
      const err = new Error(d.error || '');
      err.status = res.status;
      throw err;
    }
    // Берём имя файла из Content-Disposition
    const cd = res.headers.get('Content-Disposition') || '';
    const match = cd.match(/filename\*=UTF-8''(.+)/) || cd.match(/filename="?([^"]+)"?/);
    const filename = match ? decodeURIComponent(match[1]) : `report_${taskId}.pdf`;
    const blob = await res.blob();
    return { blob, filename };
  },

  /**
   * Отчёт об инспекции (Умная камера).
   *
   * ⚠️ Только через fetch с токеном. Обычная ссылка <a href> уходит без
   * заголовка Authorization, бэкенд отвечает 401, и просмотрщик показывает
   * пустой чёрный экран — без единой ошибки в консоли.
   */
  // Бланк осмотра по чек-листу (бумажная форма) — тот же приём fetch + токен.
  downloadChecklistPdf: async (taskId, lang = null, templateId = null) => {
    const token = getToken();
    const res = await fetch(`${BASE}/inspections/${taskId}/checklist.pdf${(() => { const q = new URLSearchParams(); if (lang) q.set('lang', lang); if (templateId) q.set('templateId', templateId); return q.toString() ? `?${q}` : ''; })()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      const err = new Error(d.error || '');
      err.status = res.status;
      throw err;
    }
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename\*=UTF-8''(.+)/) || cd.match(/filename="?([^"]+)"?/);
    return { blob: await res.blob(), filename: m ? decodeURIComponent(m[1]) : `checklist_${taskId}.pdf` };
  },

  downloadInspectionReport: async (taskId, lang = null, templateId = null) => {
    const token = getToken();
    const qs = new URLSearchParams();
    if (lang) qs.set('lang', lang);
    if (templateId) qs.set('templateId', templateId);
    const res = await fetch(`${BASE}/inspections/${taskId}/report.pdf${qs.toString() ? `?${qs}` : ''}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      const err = new Error(d.error || '');
      err.status = res.status;
      throw err;
    }
    // Имя файла берём из заголовка, а не выдумываем: бэкенд знает и назначение
    // инспекции, и номер, и название объекта.
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename\*=UTF-8''(.+)/) || cd.match(/filename="?([^"]+)"?/);
    return {
      blob: await res.blob(),
      filename: m ? decodeURIComponent(m[1]) : `inspection_${taskId}.pdf`,
    };
  },

  /**
   * Отчёт по парку оборудования Объекта — не привязан к Работе, поэтому
   * берёт propertyId, а не taskId. Тот же приём с fetch + токен, что у
   * downloadInspectionReport: обычная ссылка ушла бы без Authorization.
   */
  // scope: 'past' — ведомость «Ранее» (уехавшая техника), иначе текущий парк.
  equipmentReportPdf: async (propertyId, lang = null, templateId = null, scope = null) => {
    const token = getToken();
    const qs = new URLSearchParams({ property_id: propertyId });
    if (lang) qs.set('lang', lang);
    if (templateId) qs.set('templateId', templateId);
    if (scope) qs.set('scope', scope);
    const res = await fetch(`${BASE}/equipment/report.pdf?${qs.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      const err = new Error(d.error || 'Не удалось сформировать отчёт');
      err.status = res.status;
      err.code = d.code;
      throw err;
    }
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename\*=UTF-8''(.+)/) || cd.match(/filename="?([^"]+)"?/);
    return {
      blob: await res.blob(),
      filename: m ? decodeURIComponent(m[1]) : `equipment_${propertyId}.pdf`,
    };
  },

  // План Проекта: снимки, сравнение, журнал переносов (routes/projectBaselines.js).
  projectBaselines: (id) => request('GET', `/projects/${id}/baselines`),
  projectBaselineCreate: (id, body) => request('POST', `/projects/${id}/baselines`, body),
  projectPlanSettings: () => request('GET', '/projects/plan-settings'),
  projectPlanSettingsSave: (body) => request('PUT', '/projects/plan-settings', body),
  projectBaselineDelete: (id, bid) => request('DELETE', `/projects/${id}/baselines/${bid}`),
  projectCompare: (id, { a = null, b = null, minDays = 0 } = {}) => {
    const qs = new URLSearchParams();
    if (a) qs.set('a', a);
    if (b) qs.set('b', b);
    if (minDays) qs.set('min_days', minDays);
    return request('GET', `/projects/${id}/compare${qs.toString() ? `?${qs}` : ''}`);
  },
  projectDateChanges: (id, { entity = null, entityId = null, limit = 100 } = {}) => {
    const qs = new URLSearchParams({ limit });
    if (entity) qs.set('entity', entity);
    if (entityId) qs.set('entity_id', entityId);
    return request('GET', `/projects/${id}/date-changes?${qs}`);
  },

  // Экспорт Ганта Проекта: fmt = 'pdf' | 'xlsx'. zoom — масштаб с экрана, lang — язык документа.
  projectGanttExport: async (projectId, fmt, { lang = null, zoom = null, compare = null, journal = false, shifted = false } = {}) => {
    const token = getToken();
    const qs = new URLSearchParams();
    if (lang) qs.set('lang', lang);
    if (zoom) qs.set('zoom', zoom);
    if (compare) qs.set('compare', compare); // 'plan' | id снимка — сравнение со снимком плана
    if (journal) qs.set('journal', '1'); // приложить журнал переносов
    if (shifted) qs.set('shifted', '1'); // только Работы с отклонением (вместе с compare)
    const res = await fetch(`${BASE}/projects/${projectId}/gantt.${fmt}${qs.toString() ? `?${qs}` : ''}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      const err = new Error(d.error || '');
      err.status = res.status; err.code = d.code;
      throw err;
    }
    const raw = res.headers.get('X-Filename');
    return { blob: await res.blob(), filename: raw ? decodeURIComponent(raw) : `gantt_${projectId}.${fmt}` };
  },

  // lang — разовое переключение языка письма в превью (иначе язык коммуникаций клиента)
  getClientReportPreview: (taskId, templateId, lang) => request('GET', `/tasks/${taskId}/send-report/preview?templateId=${templateId}${lang ? `&lang=${lang}` : ''}`),
  // recipients: { to: [emails], copy_self } — из модала превью письма.
  // FormData передаёт всё строками, массив шлём JSON'ом (бэк: parseToField).
  sendClientReport: (taskId, templateId, pdfBlob, pdfFilename, recipients = null) => {
    const token = getToken();
    const fd = new FormData();
    fd.append('templateId', templateId);
    if (recipients?.to?.length) fd.append('to', JSON.stringify(recipients.to));
    if (recipients?.copy_self) fd.append('copy_self', 'true');
    if (recipients?.lang) fd.append('lang', recipients.lang);
    if (pdfBlob) fd.append('pdf', pdfBlob, pdfFilename || 'report.pdf');
    return fetch(`${BASE}/tasks/${taskId}/send-report`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    }).then(async res => {
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Send failed');
      return data;
    });
  },

  // Quotations — прейскурант (позиции и кейсы)
  getPriceItems: (params) => {
    const clean = params && typeof params === 'object'
      ? Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
      : (params ? { lang: params } : {});
    const qs = Object.keys(clean).length ? `?${new URLSearchParams(clean).toString()}` : '';
    return request('GET', `/quotations/items${qs}`);
  },
  createPriceItem: (data) => request('POST', '/quotations/items', data),
  updatePriceItem: (id, data) => request('PATCH', `/quotations/items/${id}`, data),
  deletePriceItem: (id) => request('DELETE', `/quotations/items/${id}`),
  // Категории прайса (дерево)
  getPriceCategories: (lang) => request('GET', `/quotations/categories${lang ? `?lang=${lang}` : ''}`),
  createPriceCategory: (data) => request('POST', '/quotations/categories', data),
  updatePriceCategory: (id, data) => request('PATCH', `/quotations/categories/${id}`, data),
  deletePriceCategory: (id) => request('DELETE', `/quotations/categories/${id}`),
  mergePriceCategory: (id, into) => request('POST', `/quotations/categories/${id}/merge`, { into }),
  // Словарь единиц измерения (org-scoped, закрытый — см. services/priceUnits.js).
  // lang нужен только для тенантских единиц: у системных подпись берётся из i18n
  // по ключу priceList.unit.<code>, сервер отдаёт по ним label: null.
  getPriceUnits: (lang, withUsage) => request('GET', `/quotations/units${
    lang || withUsage ? `?${[lang ? `lang=${lang}` : '', withUsage ? 'with_usage=1' : ''].filter(Boolean).join('&')}` : ''
  }`),
  createPriceUnit: (data) => request('POST', '/quotations/units', data),
  updatePriceUnit: (id, data) => request('PATCH', `/quotations/units/${id}`, data),
  deletePriceUnit: (id) => request('DELETE', `/quotations/units/${id}`),
  mergePriceUnit: (id, targetId) => request('POST', `/quotations/units/${id}/merge`, { target_id: targetId }),
  // Сметы (quotes)
  // Смета живёт либо в проекте, либо в Работе вне проекта — фильтр по одному из них.
  getQuotes: (projectId) => request('GET', `/quotes${projectId ? `?project_id=${projectId}` : ''}`),
  getTaskQuotes: (taskId) => request('GET', `/quotes?task_id=${taskId}`),
  // Реестр Смет (раздел меню). Тот же роут без project_id/task_id, но отдаёт
  // { items, total } вместо массива: у реестра есть пагинация.
  getQuotesRegistry: ({ stage, search, limit, offset, sort, dir } = {}) => {
    const p = new URLSearchParams();
    if (stage) p.set('stage', Array.isArray(stage) ? stage.join(',') : stage);
    if (search) p.set('search', search);
    if (limit != null) p.set('limit', limit);
    if (offset) p.set('offset', offset);
    if (sort) p.set('sort', sort);
    if (dir) p.set('dir', dir);
    return request('GET', `/quotes${p.toString() ? `?${p}` : ''}`);
  },
  getQuotesRegistrySummary: () => request('GET', '/quotes/registry/summary'),
  getQuote: (id) => request('GET', `/quotes/${id}`),
  createQuote: (data) => request('POST', '/quotes', data),
  updateQuote: (id, data) => request('PATCH', `/quotes/${id}`, data),
  deleteQuote: (id) => request('DELETE', `/quotes/${id}`),
  // Исполнители по смете: кому доступна вся смета / раздел / позиция.
  // PUT заменяет набор на ОДНОЙ цели целиком — пустой user_ids снимает
  // назначение и возвращает наследование (позиция → раздел → смета).
  // Работает и на замороженной смете: это раздача доступа, а не правка документа.
  getQuoteAssignees: (id) => request('GET', `/quotes/${id}/assignees`),
  setQuoteAssignees: (id, scope, scopeId, userIds) =>
    request('PUT', `/quotes/${id}/assignees`, { scope, scope_id: scopeId, user_ids: userIds }),
  duplicateQuote: (id) => request('POST', `/quotes/${id}/duplicate`),
  versionQuote: (id) => request('POST', `/quotes/${id}/version`),
  quoteSend: (id) => request('POST', `/quotes/${id}/send`),
  // Согласование админом «за клиента»: channel — как клиент подтвердил
  // (email / whatsapp / verbal), note — свободный комментарий. В этот же момент
  // смете присваивается номер документа = номер будущего счёта.
  quoteFromInspection: (taskId, title) => request('POST', '/quotes/from-inspection', { task_id: taskId, title }),
  quoteSubmitReview: (id) => request('POST', `/quotes/${id}/submit-review`),
  quoteReview: (id, body) => request('POST', `/quotes/${id}/review`, body),
  quoteApprove: (id, body) => request('POST', `/quotes/${id}/approve`, body || {}),
  quoteManualCharge: (id) => request('POST', `/quotes/${id}/manual-charge`, {}),
  quoteReject: (id) => request('POST', `/quotes/${id}/reject`),
  // Отмена согласованной сметы: снимает approved и удаляет черновик счёта в Zoho.
  // Номер остаётся за сметой и повторно не выдаётся.
  quoteCancel: (id) => request('POST', `/quotes/${id}/cancel`),
  // Ретрай присвоения номера — когда при согласовании Zoho был недоступен.
  quoteAssignNumber: (id) => request('POST', `/quotes/${id}/assign-number`),

  // ── Перерасход по замороженным сметам ───────────────────────────────────────
  // Позиции, где факт обогнал план. scope — либо проект, либо Работа вне проекта.
  getOverrun: ({ projectId, taskId }) =>
    request('GET', `/quotes/overrun?${projectId ? `project_id=${projectId}` : `task_id=${taskId}`}`),
  // Собрать черновик сметы по перерасходу. title передаём с фронта — название
  // сметы это данные тенанта, сервер его не сочиняет (иначе приедет мимо i18n).
  createQuoteFromOverrun: (data) => request('POST', '/quotes/from-overrun', data),
  // Предложение переноса перерасхода на позиции согласованной сметы.
  getOverrunTransfer: (quoteId) => request('GET', `/quotes/${quoteId}/overrun-transfer`),
  // Применить подтверждённые пары: { transfers: [{from_line_id, to_line_id, qty}] }
  applyOverrunTransfer: (quoteId, transfers) =>
    request('POST', `/quotes/${quoteId}/overrun-transfer`, { transfers }),
  // Отказ: цифры не трогаем, вопрос закрываем — баннер больше не всплывает.
  dismissOverrunTransfer: (quoteId) => request('POST', `/quotes/${quoteId}/overrun-transfer/dismiss`),
  // Выставить счёт, зарезервированный при согласовании (вся смета, как есть).
  // Частичное выставление делается через «Доходы» → «Начислить».
  // as_draft перебивает дефолт организации (как split-button в форме начисления).
  // billingProfileId — выбор в модале «На кого выставляем счёт?»: контакт
  // резерва мог быть выбран при согласовании, а платит другое юрлицо.
  quoteIssueInvoice: (id, asDraft, billingProfileId = null) => request('POST', `/quotes/${id}/issue-invoice`, {
    ...(asDraft != null ? { as_draft: !!asDraft } : {}),
    ...(billingProfileId ? { billing_profile_id: billingProfileId } : {}),
  }),
  // Отправка сметы клиенту: превью письма (тема + HTML + имя вложения) и сама отправка.
  // lang — разовое переключение языка (иначе язык коммуникаций клиента),
  // templateId — выбранный шаблон PDF (иначе первый шаблон нужного языка).
  quoteSendPreview: (id, lang, templateId) => {
    const p = new URLSearchParams();
    if (lang) p.set('lang', lang);
    if (templateId) p.set('templateId', templateId);
    const qs = p.toString();
    return request('GET', `/quotes/${id}/send-preview${qs ? `?${qs}` : ''}`);
  },
  quoteSendEmail: (id, data = {}) => request('POST', `/quotes/${id}/send-email`, data),
  // PDF сметы по шаблону тенанта. Возвращает { blob, filename }.
  quotePdf: async (id, lang, templateId) => {
    const token = getToken();
    const p = new URLSearchParams();
    if (lang) p.set('lang', lang);
    if (templateId) p.set('templateId', templateId);
    const qs = p.toString() ? `?${p.toString()}` : '';
    const res = await fetch(`${BASE}/quotes/${id}/pdf${qs}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.error || 'Не удалось сформировать PDF сметы');
    }
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename\*=UTF-8''(.+)/) || cd.match(/filename="?([^"]+)"?/);
    return { blob: await res.blob(), filename: m ? decodeURIComponent(m[1]) : `quote_${id}.pdf` };
  },
  // Превью PDF несохранённого шаблона отчёта (демо-Работа с фото) → { blob }
  reportTemplatePreview: async (payload) => {
    const token = getToken();
    const res = await fetch(`${BASE}/report-templates/preview.pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.error || 'Не удалось собрать превью');
    }
    return { blob: await res.blob(), filename: 'report-preview.pdf' };
  },

  // Превью СОХРАНЁННОГО шаблона на выбранном языке — кнопка «глаз» в модале
  // выбора. Фото не грузятся (бэкенд рендерит в режиме light), поэтому быстро.
  reportTemplatePreviewSaved: async (id, lang) => {
    const token = getToken();
    const res = await fetch(`${BASE}/report-templates/${id}/preview.pdf?lang=${lang || uiDocLang()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.error || 'Не удалось собрать превью');
    }
    return { blob: await res.blob() };
  },
  // Шаблоны PDF смет (конструктор блоков, Настройки → Шаблон сметы)
  getQuoteTemplates: () => request('GET', '/quote-templates'),
  // Блоки по умолчанию для языка — заполняют редактор нового шаблона
  getQuoteTemplateDefaults: (lang) => request('GET', `/quote-templates/defaults?lang=${lang || uiDocLang()}`),
  createQuoteTemplate: (data) => request('POST', '/quote-templates', data),
  updateQuoteTemplate: (id, data) => request('PUT', `/quote-templates/${id}`, data),
  deleteQuoteTemplate: (id) => request('DELETE', `/quote-templates/${id}`),
  // Превью PDF несохранённого шаблона → { blob }
  quoteTemplatePreview: async (payload) => {
    const token = getToken();
    const res = await fetch(`${BASE}/quote-templates/preview.pdf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.error || 'Не удалось собрать превью');
    }
    return { blob: await res.blob(), filename: 'quote-preview.pdf' };
  },
  // Контроль исполнения (work_facts): списание объёма по позициям замороженной сметы
  getWorkFactLines: (taskId) => request('GET', `/work-facts/lines?task_id=${taskId}`),
  // quoteLineId опционален — нужен, чтобы найти именно свои факты по одной строке для удаления из тайла.
  getWorkFacts: (taskId, quoteLineId) => request('GET', `/work-facts?task_id=${taskId}${quoteLineId ? `&quote_line_id=${quoteLineId}` : ''}`),
  submitWorkFact: (data) => request('POST', '/work-facts', data),
  deleteWorkFact: (id) => request('DELETE', `/work-facts/${id}`),
  getProjectControl: (projectId) => request('GET', `/quotes/control/${projectId}`),
  getProjectsControlSummary: () => request('GET', '/quotes/control/summary'),
  // Разделы смет отдельных ручек больше не имеют: раздел — часть сметы и
  // уходит в теле updateQuote/createQuote (поле sections).
  // Умный импорт сметы: файл разбирает БЭК (объединённые ячейки, двухэтажные
  // шапки, группы затрат, сверка итогов — браузерному SheetJS это недоступно).
  // Ручка ничего не пишет, только возвращает разбор.
  analyzeQuoteImport: (file) => {
    const token = getToken();
    const fd = new FormData();
    fd.append('file', file);
    return fetch(`${BASE}/quote-import/analyze`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    }).then(async (r) => {
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw Object.assign(new Error(d.error || 'Ошибка разбора'), { code: d.code, maxMb: d.maxMb });
      return d;
    });
  },
  importCases: (data) => request('POST', '/quotes/import-cases', data), // комплекты «в составе» → кейсы
  // Ручные строки сметы → позиции прайса. { lines:[{key,name,unit,nature,price_minor,cost_minor,currency}] }
  // → { results:[{key,item_id,created}] }. created=false ⇒ такая позиция уже была, цену не трогали.
  promoteQuoteLines: (lines) => request('POST', '/quotes/promote-lines', { lines }),
  // Запомнить, что по этим позициям предложение уже показывали (в т.ч. на замороженной смете).
  markQuotePromoteSeen: (id, sigs) => request('POST', `/quotes/${id}/promote-seen`, { sigs }),
  // Импорт прайса v2
  priceImportDryRun: (data) => request('POST', '/price-import/dry-run', data),
  priceImportCommit: (data) => request('POST', '/price-import/commit', data),
  getPriceImportProfiles: () => request('GET', '/price-import/profiles'),
  savePriceImportProfile: (data) => request('POST', '/price-import/profiles', data),
  getPriceCases: (lang) => request('GET', `/quotations/cases${lang ? `?lang=${lang}` : ''}`),
  createPriceCase: (data) => request('POST', '/quotations/cases', data),
  updatePriceCase: (id, data) => request('PATCH', `/quotations/cases/${id}`, data),
  deletePriceCase: (id) => request('DELETE', `/quotations/cases/${id}`),

  // AMC — Annual Maintenance Contracts
  getAmcContracts: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request('GET', `/amc${q ? `?${q}` : ''}`);
  },
  getAmcContract:    (id)       => request('GET',    `/amc/${id}`),
  createAmcContract: (data)     => request('POST',   '/amc', data),
  updateAmcContract: (id, data) => request('PATCH',  `/amc/${id}`, data),
  deleteAmcContract: (id)       => request('DELETE', `/amc/${id}`),
  updateAmcItems:    (id, items) => request('PATCH', `/amc/${id}/items`, { items }),
  getAmcPayments:    (id)            => request('GET',    `/amc/${id}/payments`),
  createAmcPayment:  (id, data)      => request('POST',   `/amc/${id}/payments`, data),
  updateAmcPayment:  (id, pid, data) => request('PATCH',  `/amc/${id}/payments/${pid}`, data),
  deleteAmcPayment:  (id, pid)       => request('DELETE', `/amc/${id}/payments/${pid}`),

  // Projects
  getProjects: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request('GET', `/projects${q ? `?${q}` : ''}`);
  },
  getProject: (id) => request('GET', `/projects/${id}`),
  createProject: (data) => request('POST', '/projects', data),
  updateProject: (id, data) => request('PUT', `/projects/${id}`, data),
  deleteProject: (id) => request('DELETE', `/projects/${id}`),
  // Phases
  createPhase: (projectId, data) => request('POST', `/projects/${projectId}/phases`, data),
  updatePhase: (projectId, phaseId, data) => request('PUT', `/projects/${projectId}/phases/${phaseId}`, data),
  deletePhase: (projectId, phaseId) => request('DELETE', `/projects/${projectId}/phases/${phaseId}`),
  // Project tasks
  addTaskToProject: (projectId, data) => request('POST', `/projects/${projectId}/tasks`, data),
  removeTaskFromProject: (projectId, taskId) => request('DELETE', `/projects/${projectId}/tasks/${taskId}`),
  moveTaskToPhase: (projectId, taskId, phaseId) => request('PATCH', `/projects/${projectId}/tasks/${taskId}/phase`, { phase_id: phaseId }),

  // Invite (публичный, без токена)
  getInvite: (inviteToken) => fetch(`${BASE}/auth/invite/${inviteToken}`).then(async r => {
    const d = await r.json();
    if (!r.ok) throw Object.assign(new Error(d.error || 'Ошибка'), { status: r.status });
    return d;
  }),
  acceptInvite: (inviteToken, password) => request('POST', '/auth/invite/accept', { token: inviteToken, password }),

  // Сброс пароля (публичный, без токена). Bare fetch — чтобы не сработал
  // глобальный 401-редирект на /login (reset-password может вернуть 401 на протухший токен).
  forgotPassword: (email) =>
    fetch(`${BASE}/auth/forgot`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }).then(async r => {
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { const e = new Error(d.error || 'error'); e.code = d.error; throw e; }
      return d;
    }),
  verifyResetOtp: (email, code) =>
    fetch(`${BASE}/auth/verify-otp`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    }).then(async r => {
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { const e = new Error(d.error || 'error'); e.code = d.error; e.left = d.left; throw e; }
      return d;
    }),
  resetPassword: (reset_token, new_password) =>
    fetch(`${BASE}/auth/reset-password`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reset_token, new_password }),
    }).then(async r => {
      const d = await r.json().catch(() => ({}));
      // status нужен вызывающему: 401 = протух reset-токен (вернуть на ввод
      // кода), остальное — обычная ошибка. По тексту в e.code это не отличить.
      if (!r.ok) { const e = new Error(d.error || 'error'); e.code = d.error; e.status = r.status; throw e; }
      return d;
    }),

  // Projects
  getProjects: (params) => {
    const qs = new URLSearchParams();
    if (params?.property_id) qs.set('property_id', params.property_id);
    if (params?.with_tasks) qs.set('with_tasks', '1');
    const q = qs.toString();
    return request('GET', '/projects' + (q ? `?${q}` : ''));
  },
  getProject: (id) => request('GET', `/projects/${id}`),
  createProject: (data) => request('POST', '/projects', data),
  updateProject: (id, data) => request('PUT', `/projects/${id}`, data),
  deleteProject: (id) => request('DELETE', `/projects/${id}`),

  // Phases
  createPhase: (projectId, data) => request('POST', `/projects/${projectId}/phases`, data),
  updatePhase: (projectId, phaseId, data) => request('PUT', `/projects/${projectId}/phases/${phaseId}`, data),
  deletePhase: (projectId, phaseId) => request('DELETE', `/projects/${projectId}/phases/${phaseId}`),

  // Project tasks
  addTaskToProject: (projectId, data) => request('POST', `/projects/${projectId}/tasks`, data),
  removeTaskFromProject: (projectId, taskId) => request('DELETE', `/projects/${projectId}/tasks/${taskId}`),
  moveTaskToPhase: (projectId, taskId, phaseId) => request('PATCH', `/projects/${projectId}/tasks/${taskId}/phase`, { phase_id: phaseId }),

  // Noxty (внутренние Telegram-уведомления)
  noxtyBindings: () => request('GET', '/noxty/bindings'),
  noxtyConnect: (kind) => request('POST', '/noxty/connect', { kind }),
  noxtyRevoke: (binding_id) => request('POST', '/noxty/revoke', { binding_id }),
  noxtyTest: () => request('POST', '/noxty/test'),
};
